/**
 * Builder - Coordinates the voxel build system
 * 
 * Responsibilities:
 * - Owns BuildMarker instance
 * - Owns BuildPreview instance for voxel preview rendering
 * - Updates marker each frame with raycast
 * - Handles place action (sends to server)
 * - Reads state from bridge, doesn't duplicate it
 */

import * as THREE from 'three';
import { BuildMarker } from './BuildMarker';
import { BuildPreview } from './BuildPreview';
import { SnapManager } from './SnapManager';
import { useGameStore } from '../../state/store';
import type { TerrainProbe } from '../spawn/TerrainProbe';
import { triggerArmSwing } from '../scene/FirstPersonArm';
import { getBuildPreset, getBuildIsEnabled } from '../../state/buildAccessors';
import { Controls } from '../player/controls';
import { VoxelWorld } from '../voxel/VoxelWorld.js';
import { sendBinary } from '../../net/netClient';
import { attachUndoSnapPoints } from '../world/WorldManager';
import { encodeVoxelBuildIntent, VoxelBuildIntent, BuildMode, BuildPresetSnapShape, PLAYER_HEIGHT, PLAYER_RADIUS, VOXEL_SCALE, MAX_BUILD_DISTANCE, isTransparent, punchParts, voxelCentreToWorld, RAY_SOLID, RAY_LIQUID, RAY_TRANSPARENT, type RaycastHit } from '@worldify/shared';

/** Identity quaternion for un-rotated ops (e.g. the punch sphere). */
const IDENTITY_QUAT = { x: 0, y: 0, z: 0, w: 1 };


const EMPTY_SET: ReadonlySet<number> = new Set();

/**
 * Terrain source for build targeting. Voxel-field probes, not mesh raycasts — so build
 * targeting works on data that is resident but not yet meshed, and always agrees with what
 * the player collides against.
 */
export type TerrainProbeProvider = TerrainProbe;

/**
 * Builder coordinates the build system.
 */
export class Builder {
  /** The build marker (wireframe indicator) */
  private readonly marker: BuildMarker;

  /** The build preview (voxel preview rendering) */
  private readonly preview: BuildPreview;

  /** Snap point manager */
  private readonly snapManager: SnapManager;

  /** Provider for collision meshes */
  private meshProvider: TerrainProbeProvider | null = null;

  /** Reference to voxel world for preview */
  private voxelWorld: VoxelWorld | null = null;

  /** Reference to scene */
  private scene: THREE.Scene | null = null;

  /** Whether the builder has been added to a scene */
  private addedToScene = false;

  /** Whether voxel preview is enabled */
  private previewEnabled: boolean = true;

  /** Injected controls instance */
  private readonly controls: Controls;

  /** Latest camera + reusable raycaster for the punch (works outside build mode). */
  private lastCamera: THREE.Camera | null = null;
  private readonly punchRaycaster = new THREE.Raycaster();
  /** Reusable punch probe hit (voxel DDA is zero-alloc; caller supplies the target). */
  private readonly punchHit: RaycastHit = { x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0, t: 0, material: 0, gx: 0, gy: 0, gz: 0 };

  /**
   * Called after a build is applied locally (offline mode) with the modified
   * chunk keys, so the host can refresh map tiles. Set by GameCore.
   */
  public onBuildApplied: ((modifiedChunkKeys: string[]) => void) | null = null;

  constructor(controls: Controls) {
    this.controls = controls;
    this.marker = new BuildMarker();
    this.preview = new BuildPreview();
    this.snapManager = new SnapManager();

    // Register for build place + punch events from controls
    this.controls.onBuildPlace = this.handlePlace;
    this.controls.onPunch = this.handlePunch;
  }

  /**
   * Set the collision mesh provider.
   * This is typically the VoxelWorld or VoxelIntegration.
   */
  setMeshProvider(provider: TerrainProbeProvider): void {
    this.meshProvider = provider;
  }

  /**
   * Set the voxel world for preview rendering.
   * Must be called before preview will work.
   */
  setVoxelWorld(world: VoxelWorld, scene: THREE.Scene): void {
    this.voxelWorld = world;
    this.scene = scene;
    this.preview.initialize(world, scene, world.meshPool);

    // When a chunk remesh completes, let preview clear committed preview meshes.
    world.addRemeshListener((chunkKey) => {
      this.preview.onChunkRemeshed(chunkKey);
    });
  }

  /**
   * Enable or disable voxel preview rendering.
   */
  setPreviewEnabled(enabled: boolean): void {
    this.previewEnabled = enabled;
    if (!enabled) {
      this.preview.clearPreview();
    }
  }

  /**
   * Drop every build visual immediately — preview meshes, the target marker, and the snap dots
   * (deposited ones included, since they live in the same group).
   *
   * `update` is only called from updatePlayingMode, so LEAVING play does not run any of the
   * per-frame gates that would normally hide these: whatever was on screen at the moment the mode
   * changed simply stayed there, floating over the explore camera. Nothing here touches build STATE
   * — the caller owns that.
   */
  suspendVisuals(): void {
    this.preview.clearPreview();
    this.marker.hideNow();
    this.snapManager.snapPointEnabled = false;
    const store = useGameStore.getState();
    store.setBuildHasValidTarget(false);
    store.setBuildInvalidReason(null);
  }

  /**
   * Add the builder's visual elements to the scene.
   */
  addToScene(scene: THREE.Scene): void {
    if (this.addedToScene) return;
    scene.add(this.marker.getObject());
    scene.add(this.snapManager.getObject());
    this.scene = scene;
    this.addedToScene = true;
  }

  /** Snap manager (for snap-point persistence wiring). */
  getSnapManager(): SnapManager {
    return this.snapManager;
  }

  /**
   * Remove the builder's visual elements from the scene.
   */
  removeFromScene(scene: THREE.Scene): void {
    if (!this.addedToScene) return;
    scene.remove(this.marker.getObject());
    scene.remove(this.snapManager.getObject());
    this.preview.clearPreview();
    this.addedToScene = false;
  }

  /**
   * Update the builder each frame.
   * Should be called from the game loop when not spectating.
   * 
   * @param camera The camera to raycast from
   * @param playerPosition The local player's position (eye level)
   */
  update(camera: THREE.Camera, playerPosition: THREE.Vector3): void {
    // Remember the camera so the punch (which fires outside build mode, when the marker is hidden)
    // can raycast on demand.
    this.lastCamera = camera;

    // Skip if no mesh provider
    if (!this.meshProvider) return;

    // Sync snap state from bridge
    // Only enable point snaps when snapping is toggled on AND the active preset supports snaps
    const presetHasSnaps = getBuildIsEnabled()
      && getBuildPreset().snapShape !== BuildPresetSnapShape.NONE;
    this.snapManager.snapPointEnabled = useGameStore.getState().build.snapPoint && presetHasSnaps;
    this.snapManager.gridSnapEnabled = useGameStore.getState().build.snapGrid;

    // Update the marker and get valid target state.
    // On mobile, controls.castNDC positions the ray at the draggable reticle;
    // on desktop it is null and the marker uses the camera-centre ray.
    let { hasValidTarget } = this.marker.update(camera, this.meshProvider, this.controls.castNDC);
    let invalidReason: 'tooClose' | null = null;

    // Apply grid snap to marker position (before overlap check)
    if (hasValidTarget && this.snapManager.gridSnapEnabled) {
      this.snapManager.applyGridSnap(this.marker.getObject().position, VOXEL_SCALE);
    }

    // Apply point snap
    let snappedDepositedIndices: ReadonlySet<number> = EMPTY_SET;
    let snappedCurrentIndices: ReadonlySet<number> = EMPTY_SET;
    if (hasValidTarget && this.snapManager.snapPointEnabled) {
      const targetPos = this.marker.getTargetPosition();
      if (targetPos) {
        const preset = getBuildPreset();
        const rotation = this.marker.getPlacementRotation();
        const snapResult = this.snapManager.trySnap(
          preset,
          { x: targetPos.x, y: targetPos.y, z: targetPos.z },
          rotation,
        );
        if (snapResult.snapped) {
          this.marker.applySnapOffset(snapResult.delta);
          snappedDepositedIndices = snapResult.snappedDepositedIndices;
          snappedCurrentIndices = snapResult.snappedCurrentIndices;
        }
      }
    }

    // Check if build shape overlaps player or camera (for modes that add solid geometry)
    // Skip check for transparent materials (player won't collide with them)
    if (hasValidTarget) {
      const preset = getBuildPreset();
      const mode = preset.parts[0].config.mode;
      const materialIsTransparent = isTransparent(preset.parts[0].config.material);
      if ((mode === BuildMode.ADD || mode === BuildMode.FILL) && !materialIsTransparent) {
        const aabb = this.marker.getWorldAABB();
        if (aabb && this.buildOverlapsPlayer(aabb, playerPosition, camera.position)) {
          hasValidTarget = false;
          invalidReason = 'tooClose';
          this.marker.setTooCloseWarning(true);
        }
      }
    }

    // Update snap visuals AFTER snap offset and overlap check
    // Hide current-shape markers when target is invalid
    if (hasValidTarget && this.snapManager.snapPointEnabled) {
      const finalPos = this.marker.getTargetPosition();
      if (finalPos) {
        const preset = getBuildPreset();
        const rotation = this.marker.getPlacementRotation();
        this.snapManager.updateVisuals(
          preset,
          { x: finalPos.x, y: finalPos.y, z: finalPos.z },
          rotation,
          snappedDepositedIndices,
          snappedCurrentIndices,
        );
      }
    } else if (this.snapManager.snapPointEnabled) {
      this.snapManager.updateVisuals(getBuildPreset(), { x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 0, w: 1 }, EMPTY_SET, EMPTY_SET);
    }

    // Fade snap markers based on distance from camera
    if (this.snapManager.snapPointEnabled) {
      this.snapManager.updateFade(camera.position as THREE.Vector3);
    }

    // Update store with valid target state and reason
    useGameStore.getState().setBuildHasValidTarget(hasValidTarget);
    useGameStore.getState().setBuildInvalidReason(invalidReason);

    // Check if deferred preview can now be shown (pending group merges resolved)
    this.preview.finalizeDeferredPreview();

    // Update voxel preview
    this.updateVoxelPreview();
  }

  /**
   * Check if a build AABB overlaps the player capsule or camera position.
   * Player capsule is approximated as an AABB for speed.
   * Adds leeway at the feet (raises the check bottom) so players can
   * comfortably build floors underneath themselves.
   */
  private buildOverlapsPlayer(
    aabb: { min: THREE.Vector3; max: THREE.Vector3 },
    playerPos: THREE.Vector3,
    cameraPos: THREE.Vector3
  ): boolean {
    // Player capsule AABB: position is at eye level, feet at position.y - PLAYER_HEIGHT
    // Leeway: ignore the bottom 0.5m so floor builds under the feet are allowed
    const FEET_LEEWAY = 0.5;
    const playerMin = {
      x: playerPos.x - PLAYER_RADIUS,
      y: playerPos.y - PLAYER_HEIGHT + FEET_LEEWAY,
      z: playerPos.z - PLAYER_RADIUS,
    };
    const playerMax = {
      x: playerPos.x + PLAYER_RADIUS,
      y: playerPos.y,
      z: playerPos.z + PLAYER_RADIUS,
    };

    // AABB vs AABB overlap test (player capsule)
    const overlapsPlayer =
      aabb.min.x <= playerMax.x && aabb.max.x >= playerMin.x &&
      aabb.min.y <= playerMax.y && aabb.max.y >= playerMin.y &&
      aabb.min.z <= playerMax.z && aabb.max.z >= playerMin.z;

    if (overlapsPlayer) return true;

    // Point-in-AABB test (camera)
    const containsCamera =
      cameraPos.x >= aabb.min.x && cameraPos.x <= aabb.max.x &&
      cameraPos.y >= aabb.min.y && cameraPos.y <= aabb.max.y &&
      cameraPos.z >= aabb.min.z && cameraPos.z <= aabb.max.z;

    return containsCamera;
  }

  /**
   * Update the voxel preview based on current build state.
   */
  private updateVoxelPreview(): void {
    // Skip if preview disabled or not initialized. If a preview is still on screen (e.g. previewEnabled
    // just toggled off, or a held post-commit preview), clear it so stale preview meshes don't linger.
    if (!this.previewEnabled || !this.voxelWorld || !this.scene) {
      if (this.voxelWorld && this.scene && this.preview.hasVisiblePreview()) this.preview.clearPreview();
      return;
    }

    // Skip if build mode disabled
    if (!getBuildIsEnabled()) {
      this.preview.clearPreview();
      return;
    }

    // Skip if no valid target
    if (!useGameStore.getState().build.hasValidTarget) {
      this.preview.clearPreview();
      return;
    }

    // Get target position from marker
    const targetPos = this.marker.getTargetPosition();
    if (!targetPos) {
      this.preview.clearPreview();
      return;
    }

    // Get preset and the full placement rotation (point-out uses the face normal)
    const preset = getBuildPreset();
    const rotation = this.marker.getPlacementRotation();
    this.preview.updatePreview(targetPos, rotation, preset.parts);
  }

  /**
   * Handle place action from controls.
   */
  private handlePlace = (): void => {
    // Skip if build mode disabled
    if (!getBuildIsEnabled()) return;

    // Skip if no valid target
    if (!useGameStore.getState().build.hasValidTarget) return;

    const targetPos = this.marker.getTargetPosition();
    if (!targetPos) return;

    // Swing the first-person arm — build dips the hand down.
    triggerArmSwing('build');

    const preset = getBuildPreset();

    // Full placement rotation (point-out orients out of the targeted face); the preset's
    // parts (e.g. torch = column + lava tip) travel in a single atomic operation.
    const rotation = this.marker.getPlacementRotation();
    const parts = preset.parts;
    const center = { x: targetPos.x, y: targetPos.y, z: targetPos.z };

    const intent: VoxelBuildIntent = {
      center,
      rotation,
      parts,
    };

    let undoable = false;
    if (useGameStore.getState().useServerChunks) {
      // Multiplayer: send intent; server validates and broadcasts a commit that
      // every client (including us) applies via GameCore.handleBuildCommit.
      const encoded = encodeVoxelBuildIntent(intent);
      sendBinary(encoded);
    } else {
      // Offline: no server round-trip — apply directly to the local world.
      const operation = { center, rotation, parts };
      const modified = this.voxelWorld?.applyBuildOperation(operation) ?? [];
      if (modified.length > 0) {
        this.onBuildApplied?.(modified);
        undoable = true;
      }
    }

    // Deposit snap points at the placed position, and hang the new ones off this build's
    // undo entry so undoing the build takes them back out.
    const deposited = this.snapManager.deposit(preset, center, rotation);
    if (undoable && deposited.length > 0) attachUndoSnapPoints(deposited);

    // Hold preview visible until the (server- or locally-) triggered remesh completes.
    this.preview.holdPreview();
  };

  /**
   * Handle the left-click / tap "punch": material-filtered dig. Raycasts from the camera (or the
   * mobile reticle) to the surface, samples the material of the voxel just inside it, and carves a
   * radius-2 blob of ONLY that material (grass digs grass, not the brick next to it). The selected
   * hotbar item is irrelevant here — the match material comes from the hit voxel.
   */
  private handlePunch = (): void => {
    const camera = this.lastCamera;
    if (!camera || !this.meshProvider || !this.voxelWorld) return;

    // Cast: mobile uses the reticle NDC, desktop the camera-centre ray (mirrors BuildMarker).
    const castNDC = this.controls.castNDC;
    let ox: number, oy: number, oz: number, dx: number, dy: number, dz: number;
    if (castNDC) {
      this.punchRaycaster.setFromCamera(new THREE.Vector2(castNDC.x, castNDC.y), camera);
      ({ x: ox, y: oy, z: oz } = this.punchRaycaster.ray.origin);
      ({ x: dx, y: dy, z: dz } = this.punchRaycaster.ray.direction);
    } else {
      const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      ox = camera.position.x; oy = camera.position.y; oz = camera.position.z;
      dx = dir.x; dy = dir.y; dz = dir.z;
    }

    const hitOk = this.meshProvider.probeRay(
      ox, oy, oz, dx, dy, dz, MAX_BUILD_DISTANCE,
      RAY_SOLID | RAY_LIQUID | RAY_TRANSPARENT, this.punchHit
    );
    if (!hitOk) return;

    // The DDA reports the exact CELL it hit and that cell's dominant material, so the
    // material is read straight from voxel data. The old path had to sample the mesh
    // vertex's material attribute, because deriving a voxel from a triangle hit could land
    // in air or on the neighbouring material — that workaround is gone, along with the
    // half-voxel inset that went with it.
    const material = this.punchHit.material;
    if (material < 0) return; // ray stopped at an unloaded chunk — nothing to punch

    const center = voxelCentreToWorld(this.punchHit.gx, this.punchHit.gy, this.punchHit.gz);
    const parts = punchParts(material);

    triggerArmSwing('punch');

    if (useGameStore.getState().useServerChunks) {
      sendBinary(encodeVoxelBuildIntent({ center, rotation: IDENTITY_QUAT, parts }));
    } else {
      const modified = this.voxelWorld.applyBuildOperation({ center, rotation: IDENTITY_QUAT, parts }) ?? [];
      if (modified.length > 0) this.onBuildApplied?.(modified);
    }
  };

  /**
   * Hand the current build preview off as a commit: moves the previewed chunks into the
   * pending-commit set so the next remesh clears the stale preview mesh and restores the
   * (preview-suppressed) group. Used by undo — it mutates voxels + dispatches a remesh but,
   * unlike a place, otherwise never notifies the preview system, so the reverted chunk would
   * stay hidden behind the old preview mesh. No-op when no preview is active.
   */
  commitPreview(): void {
    this.preview.holdPreview();
  }

  /**
   * Get the build marker.
   */
  getMarker(): BuildMarker {
    return this.marker;
  }

  /**
   * Get the build preview.
   */
  getPreview(): BuildPreview {
    return this.preview;
  }

  /**
   * Dispose of resources.
   */
  dispose(): void {
    // Only clear callbacks if they're still ours
    if (this.controls.onBuildPlace === this.handlePlace) {
      this.controls.onBuildPlace = null;
    }
    if (this.controls.onPunch === this.handlePunch) {
      this.controls.onPunch = null;
    }
    this.marker.dispose();
    this.preview.dispose();
    this.snapManager.dispose();
  }
}
